import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvSection,
	EvTextInput,
	EvTitle,
	TEXT_INPUT_TYPES
} from '@evinced-private/ui-common';

import { reorder } from 'src/helpers/DnDHelper';
import { PropertyHelper } from 'src/helpers/PropertyHelper';
import { IGroupingRule } from 'src/interfaces/GroupingRule';
import { IProperty } from 'src/interfaces/Property';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { GroupingRulesLine } from '../../../components/grouping-rules-popup/GroupingRuleLine';
import { AddGroupingRulePopup } from '../../../components/property-settings/sections/grouping-settings/AddGroupingRulePopup';
import { GroupingRulesList } from '../../../components/property-settings/sections/grouping-settings/GroupingRulesList';
import { usePropertySettings } from '../context';
import { setGrouping, setMaxPages } from '../state';

import {
	GROUPING_RULES_DESCRIPTION_ID,
	GROUPING_RULES_TITLE_ID
} from './GroupingSettingsV2.constants';
import { GROUPING_LABELS, GROUPING_MORE_INFO } from './GroupingSettingsV2.labels';
import { IGroupingSettingsV2Props } from './GroupingSettingsV2.types';

import '../../../components/property-settings/sections/grouping-settings/GroupingSettings.scss';

export const GroupingSettingsV2: FC<IGroupingSettingsV2Props> = ({
	title = '',
	description = '',
	showSampleSize = true
}) => {
	const {
		state: { draft: property, errors },
		dispatch,
		registerErrorRef
	} = usePropertySettings();

	const propertyError = useMemo(
		() => errors.find((e) => e.sectionId === SETTINGS_SECTION_TYPES.GROUPING),
		[errors]
	);

	const onGroupingChange = useCallback(
		(rules: IGroupingRule[]) => {
			dispatch(setGrouping(rules));
		},
		[dispatch]
	);

	const onMaxPagesChange = useCallback(
		(count: number) => {
			dispatch(setMaxPages(count));
		},
		[dispatch]
	);

	const [isPopupOpen, setPopupOpen] = useState(false);
	const [currentRuleForEdit, setCurrentRuleForEdit] = useState<IGroupingRule | null>(null);

	const groupingRules = useMemo(
		() => PropertyHelper.getPropertyDiscoveryGroupingRules(property),
		[property]
	);

	const removeRuleFromProperty = useCallback(
		(urlPattern: string): void => {
			const updatedRules = groupingRules.filter((r) => r.urlPattern !== urlPattern);
			onGroupingChange(updatedRules);
		},
		[groupingRules, onGroupingChange]
	);

	const setRuleForEdit = useCallback((rule: IGroupingRule | null): void => {
		setCurrentRuleForEdit(rule);
		setPopupOpen(true);
	}, []);

	const onGroupingRulesUpdated = useCallback(
		(newProperty: IProperty): void => {
			const withDefault = PropertyHelper.ensurePropertyHasDefaultRule(newProperty);
			const updatedRules = PropertyHelper.getPropertyDiscoveryGroupingRules(withDefault);
			onGroupingChange(updatedRules);
		},
		[onGroupingChange]
	);

	const onReorder = useCallback(
		(fromIndex: number, toIndex: number): void => {
			const newRules = reorder(groupingRules, fromIndex, toIndex);
			onGroupingChange(newRules);
		},
		[groupingRules, onGroupingChange]
	);

	const handleMaxPagesChange = useCallback(
		(value: string): void => {
			onMaxPagesChange(parseInt(value));
		},
		[onMaxPagesChange]
	);

	const handleClosePopup = useCallback((): void => {
		setCurrentRuleForEdit(null);
		setPopupOpen(false);
	}, []);

	const handleOpenAddRule = useCallback((): void => {
		setRuleForEdit(null);
	}, [setRuleForEdit]);

	const maxPages = useMemo(() => PropertyHelper.getMaxPagesCount(property), [property]);

	return (
		<EvSection
			className="grouping-settings-section"
			ariaLabelledby={GROUPING_RULES_TITLE_ID}
			ariaDescribedby={GROUPING_RULES_DESCRIPTION_ID}
		>
			<div className="grouping-settings-content">
				<div className="section-header">
					<div className="title">
						<EvTitle
							titleText={title || GROUPING_LABELS.DEFAULT_TITLE}
							MoreInfoContent={GROUPING_MORE_INFO}
							id={GROUPING_RULES_TITLE_ID}
							descriptionId={GROUPING_RULES_DESCRIPTION_ID}
						/>
						<div className="actions">
							<div className="single-action">
								<EvButton onClick={handleOpenAddRule} type={BUTTON_TYPES.SECONDARY}>
									{GROUPING_LABELS.ADD_RULE}
								</EvButton>
								<AddGroupingRulePopup
									property={property}
									onGroupingRulesUpdated={onGroupingRulesUpdated}
									ruleForEdit={currentRuleForEdit}
									isOpen={isPopupOpen}
									showSampleSize={showSampleSize}
									onClosePopup={handleClosePopup}
								/>
							</div>
						</div>
					</div>
				</div>
				{description && <div className="description">{description}</div>}
				<div className="section-body">
					{groupingRules?.length > 1 ? (
						<GroupingRulesList
							groupingRules={groupingRules}
							onRemoveRule={removeRuleFromProperty}
							onEditRule={setRuleForEdit}
							onReorder={onReorder}
						/>
					) : (
						<GroupingRulesLine property={property} setRuleForEdit={setRuleForEdit} />
					)}
					{showSampleSize && (
						<div className="max-pages-count-rule">
							<div className="text">{`${GROUPING_LABELS.MAX_PAGES_LABEL}:`}</div>
							<EvTextInput
								ariaLabel={GROUPING_LABELS.MAX_PAGES_LABEL}
								value={maxPages}
								onChange={handleMaxPagesChange}
								type={TEXT_INPUT_TYPES.NUMBER}
								className="max-pages-count-input"
							/>
						</div>
					)}
					{propertyError && (
						<div
							ref={(el) => registerErrorRef(SETTINGS_SECTION_TYPES.GROUPING, el)}
							className="tab-error"
						>
							{propertyError.message}
						</div>
					)}
				</div>
			</div>
		</EvSection>
	);
};
